'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, FileText, BookOpen, Briefcase, HelpCircle, ArrowRight, Command } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SearchCategory } from '@/lib/search';

interface SearchResultItem {
	id: string;
	type: SearchCategory;
	title: string;
	excerpt: string;
	url: string;
	tags: string[];
	category?: string;
	relevanceScore: number;
}

interface ByCategory {
	all: number;
	blog: number;
	resources: number;
	services: number;
	faq: number;
}

const typeIcons: Record<SearchCategory & string, typeof FileText> = {
	all: Search,
	blog: BookOpen,
	resources: FileText,
	services: Briefcase,
	faq: HelpCircle,
};

const typeLabels: Record<SearchCategory & string, string> = {
	all: 'All',
	blog: 'Blog',
	resources: 'Resources',
	services: 'Services',
	faq: 'FAQ',
};

const typeColors: Record<SearchCategory & string, string> = {
	all: 'text-[var(--color-primary)]',
	blog: 'text-cyan-400',
	resources: 'text-emerald-400',
	services: 'text-violet-400',
	faq: 'text-amber-400',
};

export default function GlobalSearch(): React.ReactElement {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [results, setResults] = useState<SearchResultItem[]>([]);
	const [byCategory, setByCategory] = useState<ByCategory>({ all: 0, blog: 0, resources: 0, services: 0, faq: 0 });
	const [activeCategory, setActiveCategory] = useState<SearchCategory>('all');
	const [loading, setLoading] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const resultsRef = useRef<HTMLDivElement>(null);

	const toggle = useCallback((): void => {
		setOpen((prev) => !prev);
	}, []);

	const close = useCallback((): void => {
		setOpen(false);
		setQuery('');
		setResults([]);
		setSelectedIndex(0);
	}, []);

	// Keyboard shortcut
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent): void => {
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault();
				toggle();
			}
			if (e.key === 'Escape') {
				close();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [toggle, close]);

	// Focus input when opened
	useEffect(() => {
		if (open) {
			setTimeout(() => inputRef.current?.focus(), 50);
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [open]);

	// Search with debounce
	useEffect(() => {
		if (!query.trim()) {
			setResults([]);
			setByCategory({ all: 0, blog: 0, resources: 0, services: 0, faq: 0 });
			return;
		}

		setLoading(true);

		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		searchTimeoutRef.current = setTimeout(async () => {
			try {
				const params = new URLSearchParams({ q: query, category: activeCategory, limit: '8' });
				const res = await fetch(`/api/search?${params.toString()}&track=1`);
				const data = await res.json();
				setResults(data.results || []);
				setByCategory(data.byCategory || { all: 0, blog: 0, resources: 0, services: 0, faq: 0 });
				setSelectedIndex(0);
			} catch (err) {
				console.error('Search failed:', err);
			} finally {
				setLoading(false);
			}
		}, 200);

		return () => {
			if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		};
	}, [query, activeCategory]);

	// Track click when navigating
	const handleResultClick = (item: SearchResultItem): void => {
		fetch('/api/search', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				query,
				category: activeCategory,
				resultsCount: results.length,
				hasClick: true,
				clickTarget: item.url,
			}),
		}).catch(() => {});
		close();
	};

	const handleKeyNav = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		if (!results.length) return;

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setSelectedIndex((prev) => Math.max(prev - 1, 0));
		} else if (e.key === 'Enter' && results[selectedIndex]) {
			e.preventDefault();
			handleResultClick(results[selectedIndex]);
			window.location.href = results[selectedIndex].url;
		}
	};

	const handleCategorySwitch = (cat: SearchCategory): void => {
		setActiveCategory(cat);
		setSelectedIndex(0);
	};

	// Scroll selected into view
	useEffect(() => {
		const element = resultsRef.current?.querySelector(`[data-index="${selectedIndex}"]`) as HTMLElement | null;
		element?.scrollIntoView({ block: 'nearest' });
	}, [selectedIndex]);

	const categories: SearchCategory[] = ['all', 'blog', 'resources', 'services', 'faq'];

	return (
		<>
			{/* Header search trigger */}
			<button
				onClick={toggle}
				className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--color-muted)] border border-[var(--color-border)] rounded-lg hover:border-[var(--color-primary)]/50 hover:text-[var(--color-foreground)] transition-colors group"
				aria-label="Open search"
			>
				<Search size={16} className="group-hover:text-[var(--color-primary)] transition-colors" />
				<span className="hidden lg:inline">Search...</span>
				<span className="hidden md:inline ml-auto text-xs text-[var(--color-muted)]/70">
					<span className="sr-only">Press </span>
					<span className="px-1.5 py-0.5 text-[10px] rounded border border-[var(--color-border)]">⌘K</span>
				</span>
			</button>

			{/* Mobile search icon button */}
			<button
				onClick={toggle}
				className="md:hidden p-2 text-[var(--color-muted)] hover:text-[var(--color-primary)] transition-colors"
				aria-label="Open search"
			>
				<Search size={20} />
			</button>

			{/* Modal */}
			{open && (
				<div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4 bg-black/70 backdrop-blur-sm animate-fadeIn" onClick={close}>
					<div
						className="w-full max-w-2xl bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl overflow-hidden shadow-2xl shadow-black/50 animate-slideDown"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Search input */}
						<div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--color-border)]">
							<Search size={20} className="text-[var(--color-primary)] flex-shrink-0" />
							<input
								ref={inputRef}
								type="text"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								onKeyDown={handleKeyNav}
								placeholder="Search articles, resources, services..."
								className="flex-1 bg-transparent text-[var(--color-foreground)] placeholder:text-[var(--color-muted)] outline-none text-base"
								autoComplete="off"
								spellCheck={false}
							/>
							{query && (
								<button
									onClick={() => setQuery('')}
									className="p-1 text-[var(--color-muted)] hover:text-[var(--color-foreground)] transition-colors"
									aria-label="Clear search"
								>
									<X size={16} />
								</button>
							)}
							<button
								onClick={close}
								className="md:hidden p-1.5 text-[var(--color-muted)] hover:text-[var(--color-foreground)]"
								aria-label="Close search"
							>
								<X size={20} />
							</button>
						</div>

						{/* Category tabs */}
						{query && (
							<div className="flex gap-1 px-4 py-2 border-b border-[var(--color-border)] overflow-x-auto">
								{categories.map((cat) => {
									const count = byCategory[cat];
									const Icon = typeIcons[cat];
									return (
										<button
											key={cat}
											onClick={() => handleCategorySwitch(cat)}
											className={cn(
												'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg whitespace-nowrap transition-colors',
												activeCategory === cat
													? 'bg-[var(--color-primary)]/15 text-[var(--color-primary)]'
													: 'text-[var(--color-muted)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-surface-light)]',
											)}
										>
											<Icon size={14} />
											{typeLabels[cat]}
											<span className={cn('text-[10px]', activeCategory === cat ? 'text-[var(--color-primary)]' : 'text-[var(--color-muted)]/70')}>
												{count}
											</span>
										</button>
									);
								})}
							</div>
						)}

						{/* Results */}
						<div ref={resultsRef} className="max-h-[55vh] overflow-y-auto">
							{loading && (
								<div className="flex items-center justify-center py-10 text-[var(--color-muted)] text-sm">
									<div className="w-5 h-5 border-2 border-[var(--color-primary)]/30 border-t-[var(--color-primary)] rounded-full animate-spin mr-2" />
									Searching...
								</div>
							)}

							{!loading && query && results.length === 0 && (
								<div className="flex flex-col items-center justify-center py-12 text-center">
									<Search size={32} className="text-[var(--color-muted)]/50 mb-3" />
									<p className="text-[var(--color-foreground)] text-sm font-medium mb-1">No results found</p>
									<p className="text-[var(--color-muted)] text-xs">
										Try different keywords or browse our full content
									</p>
								</div>
							)}

							{!loading && !query && (
								<div className="py-6 px-5">
									<p className="text-xs text-[var(--color-muted)] uppercase tracking-wider mb-3">Popular searches</p>
									<div className="flex flex-wrap gap-2">
										{['battery reconditioning', 'EV repair training', 'Middle East market', 'wholesale parts', 'high voltage safety', 'garage transformation'].map((term) => (
											<button
												key={term}
												onClick={() => setQuery(term)}
												className="px-3 py-1.5 text-xs text-[var(--color-muted)] bg-[var(--color-surface-light)] rounded-full hover:bg-[var(--color-primary)]/15 hover:text-[var(--color-primary)] transition-colors"
											>
												{term}
											</button>
										))}
									</div>
									<div className="mt-6 pt-4 border-t border-[var(--color-border)]/50">
										<p className="text-xs text-[var(--color-muted)]/80 flex items-center gap-2">
											<Command size={14} />
											Press <kbd className="px-1.5 py-0.5 text-[10px] rounded border border-[var(--color-border)] bg-[var(--color-surface-light)]">Enter</kbd> to open, <kbd className="px-1.5 py-0.5 text-[10px] rounded border border-[var(--color-border)] bg-[var(--color-surface-light)]">↑↓</kbd> to navigate, <kbd className="px-1.5 py-0.5 text-[10px] rounded border border-[var(--color-border)] bg-[var(--color-surface-light)]">Esc</kbd> to close
										</p>
									</div>
								</div>
							)}

							{!loading && results.length > 0 && (
								<ul className="py-2">
									{results.map((item, idx) => {
										const Icon = typeIcons[item.type];
										return (
											<li key={item.id}>
												<a
													href={item.url}
													data-index={idx}
													onClick={() => handleResultClick(item)}
													className={cn(
														'block px-4 py-3 mx-2 my-1 rounded-xl cursor-pointer transition-all group',
														selectedIndex === idx
															? 'bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30'
															: 'hover:bg-[var(--color-surface-light)] border border-transparent',
													)}
												>
													<div className="flex items-start gap-3">
														<div className={cn('mt-0.5 flex-shrink-0', typeColors[item.type])}>
															<Icon size={18} />
														</div>
														<div className="flex-1 min-w-0">
															<div className="flex items-center gap-2 mb-0.5">
																<h4 className="text-sm font-medium text-[var(--color-foreground)] truncate group-hover:text-[var(--color-primary)] transition-colors">
																	{highlightMatch(item.title, query)}
																</h4>
																<span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] rounded bg-[var(--color-surface-light)] text-[var(--color-muted)]">
																	{typeLabels[item.type]}
																</span>
															</div>
															<p className="text-xs text-[var(--color-muted)] line-clamp-2">
																{highlightMatch(item.excerpt, query)}
															</p>
														</div>
														<ArrowRight size={16} className="mt-1 flex-shrink-0 text-[var(--color-muted)]/50 group-hover:text-[var(--color-primary)] transition-colors" />
													</div>
												</a>
											</li>
										);
									})}

									{/* View all results */}
									{byCategory.all > 8 && (
										<li className="px-4 py-3 mx-2 mt-1 text-center">
											<a
												href={`/search?q=${encodeURIComponent(query)}${activeCategory !== 'all' ? `&category=${activeCategory}` : ''}`}
												className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
											>
												View all {byCategory.all} results
												<ArrowRight size={14} />
											</a>
										</li>
									)}
								</ul>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
}

function highlightMatch(text: string, query: string): React.ReactNode {
	if (!query.trim()) return text;
	const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
	if (tokens.length === 0) return text;

	const lower = text.toLowerCase();
	// Find first matching token
	let matchIdx = -1;
	let matchLen = 0;
	for (const token of tokens) {
		const idx = lower.indexOf(token);
		if (idx !== -1 && (matchIdx === -1 || idx < matchIdx)) {
			matchIdx = idx;
			matchLen = token.length;
		}
	}

	if (matchIdx === -1) return text;

	return (
		<>
			{text.slice(0, matchIdx)}
			<strong className="text-[var(--color-primary)] font-medium not-italic">{text.slice(matchIdx, matchIdx + matchLen)}</strong>
			{text.slice(matchIdx + matchLen)}
		</>
	);
}
